import { useCallback, useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

// Type definitions for fullscreen properties
interface DocumentWithFullscreen extends Document {
  webkitFullscreenElement?: Element | null;
  msFullscreenElement?: Element | null;
}

export function useFullscreen() {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isNative = Capacitor.isNativePlatform();

  // Listen to fullscreen change events to keep state in sync
  useEffect(() => {
    if (isNative) return;

    const handleFullscreenChange = () => {
      const doc = document as DocumentWithFullscreen;
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    // Add event listeners for fullscreen changes
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    // Set initial state
    handleFullscreenChange();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'msfullscreenchange',
        handleFullscreenChange
      );
    };
  }, [isNative]);

  const openFullscreen = useCallback(() => {
    if (isNative) return;

    const elem = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
      msRequestFullscreen?: () => void;
    };

    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }

    // Note: We don't need to manually set isFullscreen(true) here anymore
    // because the event listener will handle it
  }, [isNative]);

  const closeFullscreen = useCallback(() => {
    if (isNative) return;

    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void>;
      msExitFullscreen?: () => void;
    };

    if (doc.exitFullscreen) {
      doc.exitFullscreen();
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen();
    } else if (doc.msExitFullscreen) {
      doc.msExitFullscreen();
    }

    // Note: We don't need to manually set isFullscreen(false) here anymore
    // because the event listener will handle it
  }, [isNative]);

  return { isFullscreen, openFullscreen, closeFullscreen };
}
